import { ArrowUpRight } from 'lucide-react';
import { Button } from '../ui';
import { contactPath, contactThreadPath, useContactBook, useContactEntryQuery } from '../../../lib/contact-book';
import { composerChip, renderLaneLine, type OutreachComposerMarker } from '../../../lib/outreach-lane';
import { useAgentName } from '../../../lib/use-agent-name';
import { useTranslation } from '../../../lib/i18n';
import styles from './outreach.module.css';

/**
 * The chip under a "Tell {agent}" row in the Maestro chat — "About {name}'s conversation" — so a note the
 * owner wrote from a thread reads, in the chat it was handed to, as being ABOUT that thread, and one tap
 * goes back to it (or to the contact, for a contact-level note).
 *
 * The name comes from the contact book (`composerChip`, the shared contract): a marker the book does not
 * hold renders nothing rather than a chip with no name. Mounted only for a row that carries the marker,
 * so the book read is paid for nowhere else — and that read is the shared, already-warm head. A
 * contact-level marker the head misses is resolved by its recipient id, person-scoped. A thread marker
 * carries only the outreach id, which no read answers directly, so a thread the head does not hold gets
 * no chip.
 */
export function OutreachComposerChip({ marker }: { marker: OutreachComposerMarker }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const book = useContactBook('');
  const row = { role: 'USER', metadata: { outreachComposer: marker } };
  const headChip = composerChip(row, book.kind === 'ready' ? book.contacts : []);
  // Past the head (page 1 of the browse): a contact-level marker names its recipient, so read that one
  // contact person-scoped. Only when the head is in hand and missed — never a second read for a hit.
  const fallbackRecipient = book.kind === 'ready' && !headChip && marker.recipientId ? marker.recipientId : '';
  const fallback = useContactEntryQuery(fallbackRecipient ? { recipientId: fallbackRecipient } : { personId: '' });
  const chip = headChip ?? (fallback.entry ? composerChip(row, [fallback.entry]) : null);
  if (!chip) return null;
  const href = chip.target.screen === 'contactThread' ? contactThreadPath(chip.target.id, chip.target.outreachId) : contactPath(chip.target.id);
  return (
    <div className={styles.chipRow}>
      <Button variant="text" size="tiny" href={href} endIcon={<ArrowUpRight size={16} aria-hidden />}>
        {renderLaneLine(chip.line, t as (key: string, params?: Record<string, unknown>) => string, agent.name)}
      </Button>
    </div>
  );
}
